import React, { useState } from 'react';
import { API_URLS } from '../config';
import './styles/ReferralLinksSettings.css';

// Accept links, setLinks, and onSettingsChange from props
const ReferralLinksSettings = ({ businessId, links = [], setLinks, onSettingsChange }) => {
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCopyTooltip, setShowCopyTooltip] = useState({ id: null, show: false });

  const handleLinkChange = (id, url) => {
    // Use setLinks prop to update parent state
    setLinks(prevLinks =>
      prevLinks.map(link =>
        link.id === id ? { ...link, url } : link
      )
    );
    setError('');
    setSuccessMessage('');
    // Optionally trigger preview refresh on change, or wait for save
    // if (onSettingsChange) onSettingsChange();
  };

  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setShowCopyTooltip({ id, show: true });
      setTimeout(() => {
        setShowCopyTooltip({ id: null, show: false });
      }, 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setIsSubmitting(true);

    const token = localStorage.getItem('token');
    if (!token || !businessId) {
      setError("Authentication token or Business ID is missing.");
      setIsSubmitting(false);
      return;
    }

    // Format data for API using the links prop
    const payload = links.map(link => ({
      provider_id: link.id,
      link_url: link.url // Ensure API expects link_url
    }));

    try {
      const response = await fetch(API_URLS.referralLinks(businessId), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccessMessage('Referral links updated successfully!');
        // Update parent state with potentially updated data if API returns it
        // Or just assume the current `links` state is correct
        // setLinks(formattedLinksFromResponse); // If needed

        // Trigger preview refresh after successful save
        if (onSettingsChange) {
          onSettingsChange();
        }

        setTimeout(() => setSuccessMessage(''), 5000);
      } else {
        throw new Error(data.message || 'Failed to update referral links');
      }
    } catch (error) {
      console.error('Error updating referral links:', error);
      setError(`Error updating referral links: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="referral-settings" onSubmit={handleSubmit}>
      <h3>Referral Links</h3>

      {error && <p className="error-message">{error}</p>}
      {successMessage && <p className="success-message">{successMessage}</p>}

      <div className="referral-section">
        <div className="referral-info">
          <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>Add referral links to encourage customers to leave reviews on these platforms after giving high ratings.</span>
        </div>

        <div className="links-container">
          {/* Use links prop for mapping */}
          {links.map((link) => (
            <div key={link.id} className="link-item">
              <div className="link-header">
                <div className="link-platform">
                  {/* Use link.icon directly (which now comes from API via SettingsPage) */}
                  {link.icon && <img src={link.icon} alt={link.name} className="platform-icon" />}
                  {link.name}
                </div>
              </div>

              <div className="link-url">
                <input
                  type="url"
                  value={link.url}
                  onChange={(e) => handleLinkChange(link.id, e.target.value)}
                  placeholder={`Enter your ${link.name} review page URL`}
                  className="link-input"
                />

                {link.url && (
                  <button
                    type="button"
                    className="copy-button"
                    onClick={() => copyToClipboard(link.url, link.id)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                      <path d="M4 1.5H3a2 2 0 0 0-2 2V14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V3.5a2 2 0 0 0-2-2h-1v1h1a1 1 0 0 1 1 1V14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1h1v-1z"/>
                      <path d="M9.5 1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1-.5-.5v-1a.5.5 0 0 1 .5-.5h3zm-3-1A1.5 1.5 0 0 0 5 1.5v1A1.5 1.5 0 0 0 6.5 4h3A1.5 1.5 0 0 0 11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3z"/>
                    </svg>
                  </button>
                )}

                <span className={`copy-tooltip ${showCopyTooltip.id === link.id && showCopyTooltip.show ? 'show' : ''}`}>
                  Copied!
                </span>
              </div>

              <p className="link-description">
                This link will be shown to customers who give a high rating.
              </p>
            </div>
          ))}
        </div>
      </div>

      <button
        className="submit-button"
        type="submit"
        disabled={isSubmitting}
      >
        {isSubmitting ? (
          <>
            <span className="loading-spinner"></span>
            Saving...
          </>
        ) : 'Save Changes'}
      </button>
    </form>
  );
};

export default ReferralLinksSettings;